"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import {
  motion,
  useScroll,
  useSpring,
  useTransform,
  useInView,
  useReducedMotion,
  useMotionValue,
  useMotionTemplate,
  animate,
} from "framer-motion";
import Lenis from "lenis";
import "lenis/dist/lenis.css";
import { useAuth } from "@crossmint/client-sdk-react-ui";
import {
  ArrowRight,
  LogIn,
  Link2,
  Smartphone,
  CreditCard,
  Zap,
  TrendingUp,
  Code2,
  ShieldCheck,
  Globe,
  Wallet,
  QrCode,
  Check,
  Menu,
  X,
  UserPlus,
  Share2,
  Nfc,
  Webhook,
  Clock,
  Layers,
  CircleDollarSign,
  Lock,
  Landmark,
} from "lucide-react";

const GREEN = "#61CE70";

/* ───────────────── subtle particle field (tasteful, not loud) ───────────────── */
function ParticleField() {
  const ref = useRef<THREE.Points>(null);
  const positions = useMemo(() => {
    const N = 1400;
    const arr = new Float32Array(N * 3);
    for (let i = 0; i < N; i++) {
      arr[i * 3] = (Math.random() - 0.5) * 16;
      arr[i * 3 + 1] = (Math.random() - 0.5) * 10;
      arr[i * 3 + 2] = (Math.random() - 0.5) * 10;
    }
    return arr;
  }, []);

  useFrame((state, delta) => {
    if (!ref.current) return;
    ref.current.rotation.y += delta * 0.015;
    // gentle mouse parallax
    const px = state.pointer.x * 0.18;
    const py = state.pointer.y * 0.12;
    ref.current.rotation.x += (py - ref.current.rotation.x) * 0.03;
    ref.current.position.x += (px - ref.current.position.x) * 0.03;
  });

  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
      </bufferGeometry>
      <pointsMaterial
        size={0.028}
        color={GREEN}
        transparent
        opacity={0.55}
        sizeAttenuation
        depthWrite={false}
      />
    </points>
  );
}

function HeroCanvas() {
  return (
    <Canvas
      camera={{ position: [0, 0, 7], fov: 50 }}
      dpr={[1, 1.5]}
      gl={{ antialias: true, alpha: true }}
      style={{ pointerEvents: "none" }}
    >
      <ParticleField />
    </Canvas>
  );
}

/* ───────────────── product mockup card (live, RocketRamp-style hero visual) ─────────────────
   One timer drives everything: a new payment arrives → the latest-payment row
   animates in AND the running total ticks up by the exact same amount AND a new
   bar slides into the chart. Everything stays in sync. */
const PAYERS = [
  { who: "@sarah", method: "CashApp", logo: "/cashapp-logo.png" },
  { who: "@mike", method: "Apple Pay", logo: "/applepay-logo.png" },
  { who: "@emma", method: "Google Pay", logo: "/googlepay-logo.png" },
  { who: "@david", method: "CashApp", logo: "/cashapp-logo.png" },
  { who: "@olivia", method: "Apple Pay", logo: "/applepay-logo.png" },
  { who: "@noah", method: "Google Pay", logo: "/googlepay-logo.png" },
];
const DAY_LABELS = ["M", "T", "W", "T", "F", "S", "S"];

function DashboardMockup() {
  const [bars, setBars] = useState<number[]>([38, 58, 46, 72, 54, 84, 66]);
  const [latest, setLatest] = useState({ ...PAYERS[0], amt: 25 });
  const [tick, setTick] = useState(0);
  const total = useSpring(1725.14, { stiffness: 55, damping: 18 });
  const display = useTransform(
    total,
    (v) =>
      `$${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
  );

  useEffect(() => {
    let i = 0;
    const id = setInterval(() => {
      const amt = Math.round((6 + Math.random() * 74) * 100) / 100;
      const p = PAYERS[i++ % PAYERS.length];
      total.set(total.get() + amt); // balance climbs by the exact amount…
      setBars((prev) => [...prev.slice(1), Math.min(96, 30 + (amt / 80) * 66)]); // …new bar…
      setLatest({ ...p, amt }); // …and the row updates — all on the same beat
      setTick((t) => t + 1);
    }, 2600);
    return () => clearInterval(id);
  }, [total]);

  return (
    <div className="relative">
      <div className="pointer-events-none absolute -inset-6 rounded-[2.5rem] bg-[#61CE70]/15 blur-[60px]" />

      <motion.div
        animate={{ y: [0, -10, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        className="relative rounded-[28px] p-5 sm:p-6 w-full max-w-[380px] mx-auto overflow-hidden"
        style={{
          background:
            "linear-gradient(160deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02))",
          boxShadow:
            "0 40px 90px -30px rgba(0,0,0,0.8), inset 0 0 0 1px rgba(255,255,255,0.08)",
          backdropFilter: "blur(12px)",
        }}
      >
        {/* header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#61CE70] to-[#3aa64a] flex items-center justify-center text-[#06250f] font-black text-sm">
              J
            </div>
            <div className="leading-tight">
              <div className="text-sm font-bold text-white">@john</div>
              <div className="text-[11px] text-gray-400">cashtap.cash/john</div>
            </div>
          </div>
        </div>

        {/* live total + chart */}
        <div className="mt-6">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] text-gray-500 font-semibold">
                <span className="relative flex w-1.5 h-1.5">
                  <span className="absolute inline-flex w-full h-full rounded-full bg-[#61CE70] opacity-60 animate-ping" />
                  <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-[#61CE70]" />
                </span>
                Payments received
              </div>
              <div className="mt-1.5 flex items-end gap-2">
                <motion.span className="text-[28px] font-extrabold text-white tracking-tight tabular-nums">
                  {display}
                </motion.span>
                <span className="mb-1 inline-flex items-center gap-1 text-xs font-bold text-[#61CE70]">
                  <TrendingUp size={13} /> +12%
                </span>
              </div>
            </div>
            <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-white/5 text-gray-300">
              7D
            </span>
          </div>

          <div className="mt-4 flex items-end gap-1.5 h-[88px]">
            {bars.map((h, i) => {
              const last = i === bars.length - 1;
              return (
                <div
                  key={i}
                  className="flex-1 flex flex-col items-center justify-end h-full gap-1.5"
                >
                  <motion.div
                    animate={{ height: `${h}%` }}
                    transition={{ type: "spring", stiffness: 120, damping: 18 }}
                    className={`w-full rounded-md ${last ? "bg-[#61CE70]" : "bg-gradient-to-t from-[#61CE70]/20 to-[#61CE70]/65"}`}
                    style={
                      last
                        ? { boxShadow: "0 0 16px rgba(97,206,112,0.65)" }
                        : undefined
                    }
                  />
                  <span className="text-[9px] text-gray-600 leading-none">
                    {DAY_LABELS[i]}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* latest payment — re-animates on every new payment (key = tick) */}
        <div className="mt-5 pt-4 border-t border-white/8 min-h-[3.25rem]">
          <motion.div
            key={tick}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="flex items-center gap-3"
          >
            <span className="w-9 h-9 rounded-xl bg-white flex items-center justify-center shrink-0">
              <img
                src={latest.logo}
                alt=""
                className="max-h-4 max-w-5 object-contain"
              />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-semibold text-white">
                Payment from {latest.who}
              </div>
              <div className="text-[11px] text-gray-500">
                {latest.method} · just now
              </div>
            </div>
            <motion.div
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{
                delay: 0.1,
                type: "spring",
                stiffness: 320,
                damping: 16,
              }}
              className="text-sm font-bold text-[#61CE70] tabular-nums"
            >
              +${latest.amt.toFixed(2)}
            </motion.div>
          </motion.div>
        </div>

        {/* floating "received" pulse */}
        <motion.div
          key={`toast-${tick}`}
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: [0, 1, 1, 0], y: [-8, 0, 0, -8] }}
          transition={{
            duration: 2.4,
            times: [0, 0.12, 0.7, 1],
            ease: "easeOut",
          }}
          className="absolute top-4 right-4 flex items-center gap-1.5 rounded-full pl-1.5 pr-2.5 py-1 bg-[#61CE70]/15 ring-1 ring-[#61CE70]/30"
        >
          <span className="w-4 h-4 rounded-full bg-[#61CE70] flex items-center justify-center">
            <Check size={10} className="text-[#06250f]" strokeWidth={3.5} />
          </span>
          <span className="text-[11px] font-bold text-[#61CE70] tabular-nums">
            +${latest.amt.toFixed(2)}
          </span>
        </motion.div>
      </motion.div>
    </div>
  );
}

/* ───────────────── data ───────────────── */
const STEPS = [
  {
    icon: UserPlus,
    title: "Create your account",
    desc: "Sign up in seconds with email or Google. No paperwork, no waiting.",
  },
  {
    icon: Share2,
    title: "Share your link or QR",
    desc: "Pick how you want to get paid and share it with anyone, anywhere.",
  },
  {
    icon: Wallet,
    title: "Get paid instantly",
    desc: "Funds land in your balance in real time. Track everything in one place.",
  },
];

const TRUST = [
  { icon: ShieldCheck, label: "Licensed MSB (FinCEN)" },
  { icon: Globe, label: "Global reach" },
  { icon: Wallet, label: "You own your funds" },
];

// Stat cards for the "Built for getting paid" panel. Numeric ones (`count`)
// animate up from zero when scrolled into view; the rest reveal as-is.
type IconType = React.ComponentType<{ size?: number; className?: string }>;

const STATS: {
  count?: number;
  suffix?: string;
  value?: string;
  label: string;
  icon: IconType;
}[] = [
  { count: 6, suffix: "+", label: "Ways to get paid", icon: Layers },
  { value: "Instant", label: "Payouts to balance", icon: Zap },
  { value: "$0", label: "Setup fees", icon: CircleDollarSign },
  { value: "24/7", label: "Always-on", icon: Clock },
];

// Benefit rows for the "Built for getting paid" section.
const WHY_FEATURES: { icon: IconType; title: string; desc: string }[] = [
  {
    icon: Zap,
    title: "Real-time payouts",
    desc: "Funds hit your balance the second a payment clears — no holds, no batch delays.",
  },
  {
    icon: Wallet,
    title: "No fees to get started",
    desc: "No setup costs, no monthly minimums, no lock-in contracts. Keep more of every sale.",
  },
  {
    icon: ShieldCheck,
    title: "Compliant & secure",
    desc: "Bank-grade encryption and a FinCEN-registered MSB — non-custodial, so you own your funds.",
  },
];

// Deterministic 7-day trend for the live earnings chart (Mon→Sun).
const WEEK = [42, 58, 50, 68, 60, 82, 74];

// Developer-section pills + the live webhook log rows.
const DEV_FEATURES: {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  label: string;
}[] = [
  { icon: Code2, label: "Clean REST API" },
  { icon: Webhook, label: "Signed webhooks" },
  { icon: CreditCard, label: "Hosted checkout" },
];
// Event names are the real webhook catalogue (see /developers/docs#webhooks).
// `completed` repeats to keep the loop at four rows; it is interleaved so the
// three visible rows never show the same event twice in a row.
const DEV_EVENTS: { ev: string; code: number }[] = [
  { ev: "checkout.session.completed", code: 201 },
  { ev: "checkout.session.expired", code: 200 },
  { ev: "checkout.session.completed", code: 200 },
  { ev: "checkout.session.failed", code: 200 },
];

// Short looping product clips rendered with Remotion (see /remotion).
const SHOWCASE: { src: string; title: string; subtitle: string }[] = [
  {
    src: "/videos/live-payments.mp4",
    title: "Your balance, live",
    subtitle: "Every payment lands the moment someone pays",
  },
  {
    src: "/videos/tap-to-pay.mp4",
    title: "Tap to pay",
    subtitle: "Apple Pay & Google Pay, no app required",
  },
];

const METHODS: { src: string; alt: string; cls?: string }[] = [
  { src: "/cashapp-logo.png", alt: "CashApp" },
  { src: "/applepay-logo.png", alt: "Apple Pay" },
  { src: "/googlepay-logo.png", alt: "Google Pay" },
  { src: "/Visa-logo.png", alt: "Visa" },
  { src: "/mastercard-logo.png", alt: "Mastercard" },
  // These two ship with heavy internal whitespace, so they render tiny —
  // scale them up so they read at the same visual weight as the others.
  { src: "/amex.png", alt: "American Express", cls: "scale-[2]" },
  { src: "/discover.jpg", alt: "Discover", cls: "scale-[2]" },
  { src: "/coinbase-logo.png", alt: "Coinbase" },
];

/* ───────────────── page ───────────────── */
export default function LandingPage() {
  const navigate = useNavigate();
  const { status } = useAuth();
  const isLoggedIn = status === "logged-in";
  const lenisRef = useRef<Lenis | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const reduce = useReducedMotion();

  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, {
    stiffness: 120,
    damping: 30,
    mass: 0.3,
  });

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    lenisRef.current = lenis;
    let raf = 0;
    const loop = (t: number) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    const onScroll = ({ scroll }: { scroll: number }) =>
      setScrolled(scroll > 24);
    lenis.on("scroll", onScroll);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  const scrollTo = (id: string) => {
    setMenuOpen(false);
    const el = document.getElementById(id);
    if (el && lenisRef.current) lenisRef.current.scrollTo(el, { offset: -90 });
    else el?.scrollIntoView({ behavior: "smooth" });
  };

  const goAuth = () => navigate(isLoggedIn ? "/dashboard" : "/login");

  const fadeUp = {
    hidden: { opacity: 0, y: 26 },
    show: (i = 0) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] },
    }),
  };

  return (
    <div className="relative min-h-screen bg-[#07090D] text-white overflow-x-hidden antialiased selection:bg-[#61CE70]/30">
      {/* signature green cursor — same accent as the CashTap dashboard */}
      <CursorDot />

      {/* scroll progress */}
      <motion.div
        style={{ scaleX: progress }}
        className="fixed top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#61CE70] to-[#9BE8A6] origin-left z-[60]"
      />

      {/* ───── Navbar (logo only, scaled up) ───── */}
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-[#07090D]/80 backdrop-blur-xl border-b border-white/10"
            : "bg-transparent"
        }`}
      >
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <button
            onClick={() => scrollTo("top")}
            aria-label="CashTap home"
            className="flex items-center"
          >
            <img
              src="/logo.png"
              alt="CashTap"
              className="h-14 sm:h-16 w-auto object-contain"
            />
          </button>

          <div className="hidden md:flex items-center gap-9 text-sm font-medium text-gray-300">
            <button
              onClick={() => scrollTo("features")}
              className="hover:text-white transition-colors"
            >
              Features
            </button>
            <button
              onClick={() => scrollTo("bank")}
              className="hover:text-white transition-colors"
            >
              Banking
            </button>
            <button
              onClick={() => scrollTo("how")}
              className="hover:text-white transition-colors"
            >
              How it works
            </button>
            <button
              onClick={() => scrollTo("why")}
              className="hover:text-white transition-colors"
            >
              Why CashTap
            </button>
            <button
              onClick={() => scrollTo("developers")}
              className="hover:text-white transition-colors"
            >
              Developers
            </button>
          </div>

          <div className="hidden md:flex items-center gap-2.5">
            {isLoggedIn ? (
              <button
                onClick={() => navigate("/dashboard")}
                className="px-5 py-2.5 rounded-full text-sm font-bold text-[#06250f] bg-[#61CE70] hover:bg-[#6ed97d] transition-all active:scale-95"
              >
                Go to Dashboard
              </button>
            ) : (
              <>
                <button
                  onClick={() => navigate("/login")}
                  className="px-4 py-2.5 rounded-full text-sm font-semibold text-gray-200 hover:text-white hover:bg-white/5 transition-all"
                >
                  Log in
                </button>
                <button
                  onClick={() => navigate("/login")}
                  className="group px-5 py-2.5 rounded-full text-sm font-bold text-[#06250f] bg-[#61CE70] hover:bg-[#6ed97d] transition-all active:scale-95 flex items-center gap-1.5"
                  style={{
                    boxShadow: "0 10px 30px -10px rgba(97,206,112,0.7)",
                  }}
                >
                  Get started
                  <ArrowRight
                    size={15}
                    className="group-hover:translate-x-0.5 transition-transform"
                  />
                </button>
              </>
            )}
          </div>

          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="md:hidden p-2 rounded-lg text-gray-200 hover:bg-white/5"
            aria-label="Menu"
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>

        {menuOpen && (
          <div className="md:hidden bg-[#07090D]/95 backdrop-blur-xl border-b border-white/10 px-4 py-4 space-y-1">
            {[
              ["Features", "features"],
              ["How it works", "how"],
              ["Why CashTap", "why"],
              ["Developers", "developers"],
            ].map(([l, id]) => (
              <button
                key={id}
                onClick={() => scrollTo(id)}
                className="block w-full text-left px-3 py-2.5 rounded-lg text-gray-300 hover:bg-white/5 hover:text-white"
              >
                {l}
              </button>
            ))}
            <div className="pt-2 flex gap-2">
              <button
                onClick={() => navigate("/login")}
                className="flex-1 px-4 py-2.5 rounded-full text-sm font-semibold text-gray-200 bg-white/5"
              >
                Log in
              </button>
              <button
                onClick={goAuth}
                className="flex-1 px-4 py-2.5 rounded-full text-sm font-bold text-[#06250f] bg-[#61CE70]"
              >
                {isLoggedIn ? "Dashboard" : "Get started"}
              </button>
            </div>
          </div>
        )}
      </header>

      {/* ───── Hero ───── */}
      <section
        id="top"
        className="relative min-h-dvh flex items-center pt-28 pb-20"
      >
        {/* background layers */}
        <div className="absolute inset-0 z-0">
          <HeroCanvas />
        </div>
        <div className="pointer-events-none absolute inset-0 z-0 [background:radial-gradient(60%_50%_at_50%_-10%,rgba(97,206,112,0.18),transparent_70%)]" />
        <div className="pointer-events-none absolute inset-0 z-0 opacity-[0.05] [background-image:linear-gradient(rgba(255,255,255,0.6)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.6)_1px,transparent_1px)] [background-size:54px_54px] [mask-image:radial-gradient(70%_60%_at_50%_30%,black,transparent)]" />
        <div className="pointer-events-none absolute bottom-[-10%] right-[-5%] w-[34rem] h-[34rem] rounded-full bg-[#1E6BFF]/10 blur-[130px] z-0" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-8 items-center">
            {/* copy */}
            <div>
              <motion.div
                initial="hidden"
                animate="show"
                variants={fadeUp}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs font-medium text-gray-300 mb-6"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#61CE70] animate-pulse" />
                All-in-one fintech platform&nbsp;💚
              </motion.div>

              <motion.h1
                initial="hidden"
                animate="show"
                custom={1}
                variants={fadeUp}
                className="font-extrabold tracking-tight leading-[1.06] text-[2.7rem] sm:text-6xl lg:text-[4.4rem] min-h-[3.3em] sm:min-h-[2.3em] lg:min-h-[2.2em]"
              >
                <span className="sr-only">
                  Your U.S. bank, everywhere — get paid like a local and send,
                  earn and grow anywhere.
                </span>
                <Typewriter
                  reduce={reduce}
                  phrases={[
                    "Your U.S. bank, everywhere.",
                    "Get paid like a local.",
                    "Send, earn, grow anywhere.",
                  ]}
                  className="bg-gradient-to-r from-[#61CE70] via-[#9BE8A6] to-[#61CE70] bg-clip-text text-transparent"
                />
              </motion.h1>

              <motion.p
                initial="hidden"
                animate="show"
                custom={2}
                variants={fadeUp}
                className="mt-6 text-base sm:text-lg text-gray-400 max-w-xl leading-relaxed"
              >
                Send, receive, earn and collect — with payment links, invoices
                and tips built right in.{" "}
                <span className="text-white font-semibold">
                  One app. Everything
                </span>{" "}
                you need to move money like a local, wherever you are.
              </motion.p>

              {/* keyword groups — the core "everything in one app" story */}
              <motion.div
                initial="hidden"
                animate="show"
                custom={2.6}
                variants={fadeUp}
                className="mt-6 space-y-2.5"
              >
                <div className="flex flex-wrap gap-2">
                  {["Send", "Receive", "Earn", "Collect"].map((k) => (
                    <span
                      key={k}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#61CE70]/10 border border-[#61CE70]/25 text-[13px] font-semibold text-[#9BE8A6]"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-[#61CE70]" />
                      {k}
                    </span>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2">
                  {["Payment links", "Invoices", "Tips"].map((k) => (
                    <span
                      key={k}
                      className="inline-flex items-center px-3 py-1.5 rounded-full bg-white/[0.05] border border-white/10 text-[13px] font-medium text-gray-300"
                    >
                      {k}
                    </span>
                  ))}
                </div>
              </motion.div>

              <motion.div
                initial="hidden"
                animate="show"
                custom={3}
                variants={fadeUp}
                className="mt-9 flex flex-col sm:flex-row gap-3"
              >
                <button
                  onClick={goAuth}
                  className="group px-7 py-4 rounded-full text-base font-bold text-[#06250f] bg-[#61CE70] hover:bg-[#6ed97d] transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                  style={{
                    boxShadow: "0 16px 44px -14px rgba(97,206,112,0.75)",
                  }}
                >
                  {isLoggedIn ? "Go to Dashboard" : "Get started free"}
                  <ArrowRight
                    size={18}
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </button>
                <button
                  onClick={() => scrollTo("how")}
                  className="px-7 py-4 rounded-full text-base font-semibold text-white bg-white/[0.06] border border-white/10 hover:bg-white/10 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                >
                  See how it works
                </button>
              </motion.div>

              <motion.div
                initial="hidden"
                animate="show"
                custom={4}
                variants={fadeUp}
                className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-3"
              >
                {TRUST.map((t) => (
                  <span
                    key={t.label}
                    className="flex items-center gap-2 text-xs sm:text-[13px] text-gray-400"
                  >
                    <t.icon size={15} className="text-[#61CE70]" />
                    {t.label}
                  </span>
                ))}
              </motion.div>
            </div>

            {/* mockup */}
            <motion.div
              initial={{ opacity: 0, y: 30, rotate: -1 }}
              animate={{ opacity: 1, y: 0, rotate: 0 }}
              transition={{
                duration: 0.8,
                delay: 0.2,
                ease: [0.16, 1, 0.3, 1],
              }}
              className="relative"
            >
              <DashboardMockup />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ───── Methods strip — uniform logo wall ───── */}
      <Section className="py-14 sm:py-16 border-y border-white/5 bg-white/[0.015]">
        <div className="text-center mb-8">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#61CE70]">
            One link, every way to pay
          </p>
          <h3 className="mt-2 text-xl sm:text-2xl font-bold tracking-tight text-white">
            Built on the methods people already trust
          </h3>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-3.5 max-w-3xl mx-auto">
          {METHODS.map((m) => (
            <motion.div
              key={m.alt}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
              className="h-14 w-[104px] sm:w-[124px] rounded-2xl bg-white flex items-center justify-center px-4 overflow-hidden shadow-[0_8px_24px_-12px_rgba(0,0,0,0.6)] ring-1 ring-black/5 hover:-translate-y-1 transition-transform duration-300"
            >
              <img
                src={m.src}
                alt={m.alt}
                className={`max-h-7 max-w-full object-contain ${m.cls || ""}`}
              />
            </motion.div>
          ))}
        </div>
      </Section>

      {/* ───── Features — bento grid ───── */}
      <Section id="features" className="py-20 sm:py-28">
        <SectionHeading
          eyebrow="Everything you need"
          title="One platform, every payment method"
          subtitle="Stop stitching tools together. CashTap brings every way to collect money into a single, beautiful dashboard."
        />

        {/* Dashboard walkthrough (Remotion) — a real tour of the product,
            framed like the live app. */}
        <div className="mx-auto mt-14 max-w-5xl">
          <VideoCard
            src="/videos/dashboard-tour.mp4"
            aspect="aspect-video"
            chromeUrl="app.cashtap.cash/dashboard"
            title=""
            subtitle=""
          />
          <p className="mt-4 text-center text-sm text-gray-500">
            A tour of the CashTap dashboard — wallet, payments, collect, send
            &amp; analytics in one place.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {/* A — Payment links (wide) */}
          <BentoTile
            className="sm:col-span-2"
            icon={Link2}
            title="Payment links & profiles"
            desc="Requests, donation boxes, tip jars, invoices, and a shareable profile. One link, paid anywhere."
          >
            <LivePaymentRows />
          </BentoTile>

          {/* B — CashApp */}
          <BentoTile
            icon={QrCode}
            title="CashApp Pay"
            desc="One-tap CashApp checkout. Share a link or a QR and get paid in seconds."
          >
            <div className="mt-auto pt-6 flex items-end justify-center">
              <MiniQR />
            </div>
          </BentoTile>

          {/* C — Apple/Google Pay */}
          <BentoTile
            icon={Smartphone}
            title="Apple Pay & Google Pay"
            desc="Let anyone pay with the wallet already on their phone — fast and familiar."
          >
            <NfcRipple />
          </BentoTile>

          {/* D — Card & bank */}
          <BentoTile
            icon={CreditCard}
            title="Card & bank"
            desc="Debit, credit, and bank payments via a secure, compliant checkout."
          >
            <div className="mt-auto pt-6">
              <div className="relative w-28 h-16 rounded-xl bg-gradient-to-br from-[#1b2330] to-[#0e141d] ring-1 ring-white/10 p-2.5 flex flex-col justify-between overflow-hidden">
                <motion.div
                  aria-hidden
                  className="pointer-events-none absolute inset-y-0 -left-8 w-8 skew-x-[-20deg] bg-white/10"
                  animate={reduce ? {} : { left: ["-2rem", "8rem"] }}
                  transition={{
                    duration: 2.6,
                    repeat: Infinity,
                    repeatDelay: 1.4,
                    ease: "easeInOut",
                  }}
                />
                <div className="w-7 h-5 rounded bg-[#61CE70]/70" />
                <div className="flex items-center gap-1">
                  {[0, 1, 2, 3].map((i) => (
                    <span
                      key={i}
                      className="w-2 h-1 rounded-full bg-white/30"
                    />
                  ))}
                  <span className="ml-auto text-[8px] text-white/40 font-mono tracking-wide">
                    CASHTAP
                  </span>
                </div>
              </div>
            </div>
          </BentoTile>

          {/* E — Lightning */}
          <BentoTile
            icon={Zap}
            title="Lightning fast"
            desc="Bitcoin Lightning support for instant, low-fee transfers when speed matters."
          >
            <LightningPulse />
          </BentoTile>

          {/* F — Earn (full width, yield panel) */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-60px" }}
            className="group relative sm:col-span-2 lg:col-span-3 rounded-3xl p-6 sm:p-8 bg-gradient-to-br from-white/[0.05] to-transparent border border-white/10 hover:border-[#61CE70]/40 transition-all duration-300 overflow-hidden grid gap-8 lg:gap-12 md:grid-cols-2 items-center"
          >
            <div className="pointer-events-none absolute -left-16 -bottom-16 w-56 h-56 rounded-full bg-[#61CE70]/[0.08] blur-3xl" />

            {/* copy */}
            <div className="relative">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-2xl bg-[#61CE70]/12 flex items-center justify-center">
                  <TrendingUp size={20} className="text-[#61CE70]" />
                </div>
                <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#61CE70]">
                  Earn
                </span>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold tracking-tight">
                Earn with{" "}
                <motion.span
                  className="bg-gradient-to-r from-[#61CE70] via-[#9BE8A6] to-[#61CE70] bg-clip-text text-transparent"
                  style={{ backgroundSize: "200% auto" }}
                  animate={
                    reduce
                      ? undefined
                      : { backgroundPosition: ["0% 50%", "200% 50%"] }
                  }
                  transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
                >
                  CashTap
                </motion.span>
              </h3>
              <p className="mt-2.5 text-sm sm:text-[15px] text-gray-400 leading-relaxed max-w-md">
                Put idle funds to work and earn yield, right from your
                dashboard.
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                {["No lock-ups", "Withdraw anytime"].map((b, i) => (
                  <motion.span
                    key={b}
                    initial={{ opacity: 0, y: 8 }}
                    whileInView={{
                      opacity: 1,
                      y: 0,
                      transition: {
                        duration: 0.4,
                        delay: 0.15 + i * 0.1,
                        ease: [0.16, 1, 0.3, 1],
                      },
                    }}
                    viewport={{ once: true }}
                    whileHover={reduce ? undefined : { y: -2 }}
                    transition={{ type: "spring", stiffness: 320, damping: 22 }}
                    className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 text-[13px] font-medium text-gray-200 transition-colors duration-300 hover:border-[#61CE70]/35 hover:bg-[#61CE70]/[0.07]"
                  >
                    <Check
                      size={14}
                      className="text-[#61CE70]"
                      strokeWidth={3}
                    />
                    {b}
                  </motion.span>
                ))}
              </div>
              <p className="mt-5 flex items-center gap-1.5 text-sm font-semibold text-[#9BE8A6]">
                Your money, working for you
                <motion.span
                  aria-hidden
                  className="inline-block"
                  animate={
                    reduce ? undefined : { scale: [1, 1.22, 1, 1.12, 1] }
                  }
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    repeatDelay: 2.6,
                    ease: "easeInOut",
                  }}
                >
                  💚
                </motion.span>
              </p>
            </div>

            {/* yield card */}
            <div className="relative">
              <EarnYieldCard reduce={reduce} />
            </div>
          </motion.div>
        </div>
      </Section>

      {/* ───── U.S. bank account spotlight ───── */}
      <Section id="bank" className="py-20 sm:py-28">
        <div className="rounded-[2.5rem] p-6 sm:p-10 lg:p-14 bg-gradient-to-br from-[#0E1620] via-[#0B111A] to-[#0A0E15] border border-white/10 relative overflow-hidden">
          <div className="pointer-events-none absolute -left-28 -top-28 w-[26rem] h-[26rem] rounded-full bg-[#61CE70]/10 blur-[120px]" />
          <div className="pointer-events-none absolute -right-28 -bottom-28 w-[26rem] h-[26rem] rounded-full bg-[#1E6BFF]/10 blur-[130px]" />
          <div className="pointer-events-none absolute inset-0 opacity-[0.04] [background-image:linear-gradient(rgba(255,255,255,0.6)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.6)_1px,transparent_1px)] [background-size:48px_48px] [mask-image:radial-gradient(70%_60%_at_50%_40%,black,transparent)]" />

          <div className="relative grid lg:grid-cols-[0.82fr_1.18fr] gap-12 lg:gap-16 items-center">
            {/* left — value copy + benefits */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs font-medium text-gray-300 mb-5">
                <Globe size={13} className="text-[#61CE70]" /> Built for global
                users&nbsp;🌍
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-[2.9rem] font-extrabold tracking-tight leading-[1.05]">
                Get your{" "}
                <span className="bg-gradient-to-r from-[#61CE70] via-[#9BE8A6] to-[#61CE70] bg-clip-text text-transparent">
                  U.S. bank account
                </span>
              </h2>
              <p className="mt-5 text-gray-400 leading-relaxed max-w-lg">
                A real U.S. account and routing number in your name — so you can
                receive payments like a local, from anywhere in the world.
              </p>

              <div className="mt-8 space-y-3">
                {[
                  {
                    icon: Landmark,
                    title: "Routing & account number",
                    desc: "Your own U.S. account and routing number, ready to receive the moment you sign up.",
                  },
                  {
                    icon: Wallet,
                    title: "Receive payments like a local",
                    desc: "Get paid by U.S. banks, employers and platforms over ACH and wire — no middlemen.",
                  },
                  {
                    icon: Globe,
                    title: "Built for global users",
                    desc: "Open from anywhere. Designed for people without a U.S. address to bank like they're there.",
                  },
                ].map((f, i) => (
                  <motion.div
                    key={f.title}
                    initial={{ opacity: 0, x: -16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{
                      duration: 0.5,
                      delay: i * 0.1,
                      ease: [0.16, 1, 0.3, 1],
                    }}
                    className="group flex gap-4 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4 transition-colors hover:border-[#61CE70]/25 hover:bg-white/[0.04]"
                  >
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#61CE70]/12 transition-colors group-hover:bg-[#61CE70]/20">
                      <f.icon size={18} className="text-[#61CE70]" />
                    </div>
                    <div>
                      <div className="font-semibold text-white">{f.title}</div>
                      <div className="mt-0.5 text-sm text-gray-400 leading-relaxed">
                        {f.desc}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-x-6 gap-y-4">
                <button
                  onClick={goAuth}
                  className="group inline-flex shrink-0 whitespace-nowrap items-center justify-center gap-2 px-7 py-4 rounded-full text-base font-bold text-[#06250f] bg-[#61CE70] hover:bg-[#6ed97d] transition-all active:scale-[0.98]"
                  style={{
                    boxShadow: "0 16px 40px -14px rgba(97,206,112,0.6)",
                  }}
                >
                  {isLoggedIn ? "Open your account" : "Get your account"}
                  <ArrowRight
                    size={18}
                    className="shrink-0 group-hover:translate-x-1 transition-transform"
                  />
                </button>
                <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-gray-500">
                  <span className="flex items-center gap-1.5">
                    <Check
                      size={13}
                      className="text-[#61CE70]"
                      strokeWidth={3}
                    />{" "}
                    No U.S. address needed
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Check
                      size={13}
                      className="text-[#61CE70]"
                      strokeWidth={3}
                    />{" "}
                    Ready in minutes
                  </span>
                </div>
              </div>
            </div>

            {/* right — big animated U.S. account card (Remotion clip) */}
            <motion.div
              initial={{ opacity: 0, y: 26 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="relative w-full"
            >
              {/* ambient brand glow behind the card */}
              <div className="pointer-events-none absolute -inset-6 rounded-[2rem] bg-[#61CE70]/[0.06] blur-3xl" />

              <div className="group relative overflow-hidden rounded-[1.75rem] border border-white/10 shadow-[0_40px_100px_-40px_rgba(0,0,0,0.9)] transition-transform duration-300 hover:-translate-y-1">
                <AccountCardVideo />
                <div className="pointer-events-none absolute inset-0 rounded-[1.75rem] ring-1 ring-inset ring-white/10 transition group-hover:ring-[#61CE70]/30" />
              </div>
            </motion.div>
          </div>
        </div>
      </Section>

      {/* ───── How it works ───── */}
      <Section id="how" className="py-20 sm:py-28">
        <SectionHeading
          eyebrow="Get started in minutes"
          title="How CashTap works"
          subtitle="From sign-up to your first payment in three simple steps."
        />
        <div className="mt-16 relative">
          {/* animated connector: a rail that fills with green on scroll-in, with a
              pulse travelling sign-up → share → paid to show the flow (desktop). */}
          <div className="hidden md:block absolute top-9 left-[16.66%] right-[16.66%] h-[2px] -translate-y-1/2">
            <div className="absolute inset-0 rounded-full bg-white/10" />
            <motion.div
              className="absolute inset-y-0 left-0 w-full origin-left rounded-full bg-gradient-to-r from-[#61CE70]/70 via-[#61CE70]/50 to-[#61CE70]/70"
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
            />
            {!reduce && (
              <motion.span
                className="absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#61CE70]"
                style={{ boxShadow: "0 0 14px rgba(97,206,112,0.9)" }}
                initial={{ left: "0%", opacity: 0 }}
                whileInView={{ left: ["0%", "100%"], opacity: [0, 1, 1, 0] }}
                viewport={{ once: false }}
                transition={{
                  duration: 2.8,
                  ease: "easeInOut",
                  repeat: Infinity,
                  repeatDelay: 0.5,
                }}
              />
            )}
          </div>
          <div className="relative grid md:grid-cols-3 gap-10 md:gap-6">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.title}
                variants={fadeUp}
                custom={i}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                className="relative text-center"
              >
                <div className="relative mx-auto w-[72px] h-[72px]">
                  <div className="absolute inset-0 rounded-2xl bg-[#61CE70]/15 blur-md" />
                  <motion.div
                    animate={reduce ? {} : { y: [0, -6, 0] }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: i * 0.4,
                    }}
                    className="relative w-full h-full rounded-2xl bg-[#0B0E14] border border-[#61CE70]/30 flex items-center justify-center"
                  >
                    <s.icon size={26} className="text-[#61CE70]" />
                  </motion.div>
                  <span className="absolute -top-2.5 -right-2.5 w-7 h-7 rounded-full bg-[#61CE70] text-[#06250f] text-xs font-black flex items-center justify-center ring-4 ring-[#07090D]">
                    {i + 1}
                  </span>
                </div>
                <h3 className="mt-6 text-xl font-bold tracking-tight">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm text-gray-400 leading-relaxed max-w-xs mx-auto">
                  {s.desc}
                </p>
                {i === 0 && <StepAccountChip />}
                {i === 1 && <StepShareChip />}
                {i === 2 && <StepPaidChip />}
              </motion.div>
            ))}
          </div>
        </div>
        <div className="mt-12 text-center">
          <button
            onClick={goAuth}
            className="group inline-flex items-center gap-2 px-7 py-4 rounded-full text-base font-bold text-[#06250f] bg-[#61CE70] hover:bg-[#6ed97d] transition-all active:scale-[0.98]"
            style={{ boxShadow: "0 16px 40px -14px rgba(97,206,112,0.6)" }}
          >
            {isLoggedIn ? "Open your dashboard" : "Create your free account"}
            <ArrowRight
              size={18}
              className="group-hover:translate-x-1 transition-transform"
            />
          </button>
        </div>
      </Section>

      {/* ───── Product in motion (Remotion clips) ───── */}
      <Section id="showcase" className="py-20 sm:py-28">
        <SectionHeading
          eyebrow="See it in motion"
          title="Watch the money move"
          subtitle="Real product moments — payments landing, a tap checkout, an API call settling — all in a few seconds."
        />
        <div className="mt-14 grid gap-5 sm:grid-cols-2">
          {SHOWCASE.map((clip, i) => (
            <VideoCard
              key={clip.src}
              src={clip.src}
              aspect="aspect-square"
              title={clip.title}
              subtitle={clip.subtitle}
              delay={i * 0.1}
            />
          ))}
        </div>
      </Section>

      {/* ───── Why CashTap (stats / value) ───── */}
      <Section id="why" className="py-20 sm:py-28">
        <div className="rounded-[2rem] p-8 sm:p-12 bg-gradient-to-br from-[#0E1620] to-[#0A0E15] border border-white/10 relative overflow-hidden">
          <div className="pointer-events-none absolute -left-20 -top-20 w-80 h-80 rounded-full bg-[#61CE70]/10 blur-[100px]" />

          <div className="relative grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            {/* left — value copy + benefit rows */}
            <div>
              <p className="text-xs font-semibold tracking-[0.18em] uppercase text-[#61CE70] mb-3">
                Built for getting paid
              </p>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
                Money in, the moment someone pays
              </h2>
              <p className="mt-4 text-gray-400 leading-relaxed">
                No merchant account, no waiting days for settlement. CashTap is
                non-custodial, compliant, and ready the second you sign up.
              </p>

              <div className="mt-8 space-y-5">
                {WHY_FEATURES.map((f, i) => (
                  <motion.div
                    key={f.title}
                    initial={{ opacity: 0, x: -16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{
                      duration: 0.5,
                      delay: i * 0.1,
                      ease: [0.16, 1, 0.3, 1],
                    }}
                    className="group flex gap-4"
                  >
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#61CE70]/12 transition-colors group-hover:bg-[#61CE70]/20">
                      <f.icon size={18} className="text-[#61CE70]" />
                    </div>
                    <div>
                      <div className="font-semibold text-white">{f.title}</div>
                      <div className="mt-0.5 text-sm text-gray-400 leading-relaxed">
                        {f.desc}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* right — unified live earnings dashboard card */}
            <LiveEarningsPanel />
          </div>
        </div>
      </Section>

      {/* ───── Developers ───── */}
      <Section id="developers" className="py-20 sm:py-28">
        <div className="rounded-[2rem] p-8 sm:p-12 bg-gradient-to-br from-[#0F1620] to-[#0A0E15] border border-white/10 relative overflow-hidden">
          <div className="pointer-events-none absolute -right-24 -top-24 w-80 h-80 rounded-full bg-[#1E6BFF]/10 blur-[100px]" />
          <div className="relative grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <p className="flex items-center gap-2 text-xs font-semibold tracking-[0.18em] uppercase text-gray-500 mb-3">
                <Code2 size={14} className="text-[#61CE70]" /> For developers
              </p>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
                Drop-in payments API
              </h2>
              <p className="mt-4 text-gray-400 leading-relaxed">
                Integrate CashTap into your own product with a clean REST API,
                webhooks, and a hosted checkout. Go from test to live in an
                afternoon.
              </p>

              {/* animated capability pills */}
              <div className="mt-6 flex flex-wrap gap-2.5">
                {DEV_FEATURES.map((f, i) => (
                  <motion.span
                    key={f.label}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{
                      duration: 0.45,
                      delay: i * 0.1,
                      ease: [0.16, 1, 0.3, 1],
                    }}
                    className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3.5 py-2 text-[13px] font-medium text-gray-200 transition-colors hover:border-[#61CE70]/40"
                  >
                    <f.icon size={15} className="text-[#61CE70]" />
                    {f.label}
                  </motion.span>
                ))}
              </div>

              <div className="mt-7 flex flex-wrap gap-3">
                <button
                  onClick={() => navigate("/developers/docs")}
                  className="px-6 py-3.5 rounded-full text-sm font-bold text-[#06250f] bg-[#61CE70] hover:bg-[#6ed97d] transition-all active:scale-[0.98]"
                >
                  Read the docs
                </button>
                <button
                  onClick={goAuth}
                  className="px-6 py-3.5 rounded-full text-sm font-semibold text-white bg-white/5 border border-white/10 hover:bg-white/10 transition-all active:scale-[0.98]"
                >
                  Get API keys
                </button>
              </div>

              <DevEventStream />
            </div>

            {/* Remotion clip: the request → hosted checkout → webhook flow */}
            <VideoCard
              src="/videos/api-to-paid.mp4"
              aspect="aspect-video"
              title="From API call to paid"
              subtitle="checkout.create() → hosted checkout → webhook"
            />
          </div>
        </div>
      </Section>

      {/* ───── Final CTA ───── */}
      <Section className="py-20 sm:py-28">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="relative rounded-[2.25rem] px-6 py-16 sm:px-12 sm:py-20 text-center overflow-hidden ring-1 ring-[#61CE70]/25"
          style={{ background: "linear-gradient(180deg,#0E1620,#0A0E15)" }}
        >
          {/* glows + faint grid */}
          <div className="pointer-events-none absolute top-[-30%] left-1/2 -translate-x-1/2 w-[34rem] h-[34rem] rounded-full bg-[#61CE70]/18 blur-[130px]" />
          <div className="pointer-events-none absolute bottom-[-40%] right-[-10%] w-[26rem] h-[26rem] rounded-full bg-[#1E6BFF]/12 blur-[120px]" />
          <div className="pointer-events-none absolute inset-0 opacity-[0.04] [background-image:linear-gradient(rgba(255,255,255,0.7)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.7)_1px,transparent_1px)] [background-size:48px_48px] [mask-image:radial-gradient(60%_60%_at_50%_40%,black,transparent)]" />

          {/* payment chips drifting up behind the CTA — "money flowing in" */}
          <FloatingChips />

          <div className="relative">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#61CE70]/12 ring-1 ring-[#61CE70]/25 text-xs font-semibold text-[#61CE70] mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[#61CE70] animate-pulse" />{" "}
              Start free today
            </span>
            <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight leading-tight">
              Ready to start{" "}
              <span className="bg-gradient-to-r from-[#61CE70] via-[#9BE8A6] to-[#61CE70] bg-clip-text text-transparent">
                getting paid?
              </span>
            </h2>
            <p className="mt-4 text-gray-300/90 max-w-xl mx-auto text-base sm:text-lg">
              Create your free account in under a minute. No card required.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={goAuth}
                className="group px-8 py-4 rounded-full text-base font-bold text-[#06250f] bg-[#61CE70] hover:bg-[#6ed97d] transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                style={{ boxShadow: "0 16px 44px -14px rgba(97,206,112,0.8)" }}
              >
                {isLoggedIn ? "Go to Dashboard" : "Get started free"}
                <ArrowRight
                  size={18}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </button>
              <button
                onClick={() => navigate("/login")}
                className="px-8 py-4 rounded-full text-base font-semibold text-white bg-white/[0.06] border border-white/10 hover:bg-white/10 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
              >
                <LogIn size={18} /> Log in
              </button>
            </div>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
              {[
                "No card required",
                "Cancel anytime",
                "Licensed & compliant",
              ].map((t) => (
                <span
                  key={t}
                  className="flex items-center gap-1.5 text-xs text-gray-400"
                >
                  <Check size={14} className="text-[#61CE70]" /> {t}
                </span>
              ))}
            </div>
          </div>
        </motion.div>
      </Section>

      {/* ───── Footer ───── */}
      <footer className="relative border-t border-white/10 pt-16 pb-10">
        <div className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[60%] h-px bg-gradient-to-r from-transparent via-[#61CE70]/40 to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-12 gap-10">
            {/* brand */}
            <div className="col-span-2 md:col-span-5">
              <img
                src="/logo.png"
                alt="CashTap"
                className="h-12 w-auto object-contain"
              />
              <p className="mt-4 text-sm text-gray-400 max-w-xs leading-relaxed">
                Get paid by anyone, in any way. One link for CashApp, Apple Pay,
                Google Pay, cards and more.
              </p>
              <button
                onClick={goAuth}
                className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold text-[#06250f] bg-[#61CE70] hover:bg-[#6ed97d] transition-all active:scale-95"
              >
                {isLoggedIn ? "Go to Dashboard" : "Get started free"}
                <ArrowRight size={15} />
              </button>
            </div>

            {/* link columns */}
            {[
              {
                head: "Product",
                links: [
                  { label: "Features", onClick: () => scrollTo("features") },
                  { label: "Banking", onClick: () => scrollTo("bank") },
                  { label: "How it works", onClick: () => scrollTo("how") },
                  { label: "Why CashTap", onClick: () => scrollTo("why") },
                  {
                    label: "Developers",
                    onClick: () => navigate("/developers/docs"),
                  },
                ],
              },
              {
                head: "Legal",
                links: [
                  {
                    label: "Privacy Policy",
                    onClick: () => navigate("/privacy-policy"),
                  },
                  {
                    label: "Terms of Use",
                    onClick: () => navigate("/terms-of-use"),
                  },
                  {
                    label: "AML Policy",
                    onClick: () => navigate("/aml-policy"),
                  },
                ],
              },
              {
                head: "Get started",
                links: [
                  { label: "Log in", onClick: () => navigate("/login") },
                  {
                    label: "Create account",
                    onClick: () => navigate("/login"),
                  },
                  {
                    label: "Read the docs",
                    onClick: () => navigate("/developers/docs"),
                  },
                ],
              },
            ].map((col) => (
              <div key={col.head} className="md:col-span-2">
                <h4 className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-500 mb-4">
                  {col.head}
                </h4>
                <ul className="space-y-3">
                  {col.links.map((l) => (
                    <li key={l.label}>
                      <button
                        onClick={l.onClick}
                        className="text-sm text-gray-400 hover:text-white transition-colors"
                      >
                        {l.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* compliance + bottom bar */}
          <div className="mt-12 pt-7 border-t border-white/5">
            <div className="mt-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-500">
              <span>
                © {new Date().getFullYear()} CashTap. All rights reserved.
              </span>
              <span className="flex items-center gap-1.5">
                <ShieldCheck size={13} className="text-[#61CE70]" /> Secured
                with bank-grade encryption
              </span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ───────────────── helpers ───────────────── */
/* ───────────────── Typewriter ─────────────────
   Types a phrase, holds, deletes, then types the next — looping through the
   list with a blinking caret. Reduced-motion users get the first phrase
   statically. Purely decorative, so it's aria-hidden (the h1 carries a stable
   sr-only heading for screen readers). */
function Typewriter({
  phrases,
  className = "",
  reduce = false,
}: {
  phrases: string[];
  className?: string;
  reduce?: boolean | null;
}) {
  const [text, setText] = useState(phrases[0]);
  const [index, setIndex] = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (reduce) return;
    const current = phrases[index % phrases.length];

    // Finished typing → hold, then start deleting.
    if (!deleting && text === current) {
      const id = window.setTimeout(() => setDeleting(true), 1700);
      return () => clearTimeout(id);
    }
    // Finished deleting → advance to the next phrase.
    if (deleting && text === "") {
      setDeleting(false);
      setIndex((v) => (v + 1) % phrases.length);
      return;
    }
    // Type or delete one character.
    const id = window.setTimeout(
      () =>
        setText(
          deleting
            ? current.slice(0, text.length - 1)
            : current.slice(0, text.length + 1),
        ),
      deleting ? 45 : 80,
    );
    return () => clearTimeout(id);
  }, [text, deleting, index, phrases, reduce]);

  return (
    <span aria-hidden="true">
      <span className={className}>{reduce ? phrases[0] : text}</span>
      <span className="ml-1 inline-block w-[0.05em] h-[0.82em] translate-y-[0.04em] rounded-[2px] bg-[#61CE70] align-middle animate-pulse" />
    </span>
  );
}

function Section({
  id,
  className = "",
  children,
}: {
  id?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className={className}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">{children}</div>
    </section>
  );
}

function SectionHeading({
  eyebrow,
  title,
  subtitle,
}: {
  eyebrow: string;
  title: string;
  subtitle: string;
}) {
  return (
    <motion.div
      variants={{
        hidden: { opacity: 0, y: 24 },
        show: {
          opacity: 1,
          y: 0,
          transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
        },
      }}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-80px" }}
      className="text-center max-w-2xl mx-auto"
    >
      <p className="text-xs font-semibold tracking-[0.18em] uppercase text-[#61CE70] mb-3">
        {eyebrow}
      </p>
      <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight">
        {title}
      </h2>
      <p className="mt-4 text-gray-400 leading-relaxed">{subtitle}</p>
    </motion.div>
  );
}

function BentoTile({
  icon: Icon,
  title,
  desc,
  className = "",
  children,
}: {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  title: string;
  desc: string;
  className?: string;
  children?: React.ReactNode;
}) {
  return (
    <motion.div
      variants={{
        hidden: { opacity: 0, y: 24 },
        show: {
          opacity: 1,
          y: 0,
          transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
        },
      }}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-60px" }}
      className={`group relative flex flex-col rounded-3xl p-6 bg-white/[0.03] border border-white/10 hover:border-[#61CE70]/40 transition-all duration-300 hover:-translate-y-1 overflow-hidden ${className}`}
    >
      <div className="pointer-events-none absolute -right-16 -top-16 w-40 h-40 rounded-full bg-[#61CE70]/[0.07] blur-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
      <div className="w-11 h-11 rounded-2xl bg-[#61CE70]/12 flex items-center justify-center mb-4 group-hover:bg-[#61CE70]/20 transition-colors">
        <Icon size={20} className="text-[#61CE70]" />
      </div>
      <h3 className="text-lg font-bold tracking-tight">{title}</h3>
      <p className="mt-2 text-sm text-gray-400 leading-relaxed">{desc}</p>
      {children}
    </motion.div>
  );
}

// A CashApp-style QR: rounded finder "eyes", a dotted body, green $ in the
// middle. Drawn as SVG so it reads like a real scannable code, not a grid.
function MiniQR() {
  const reduce = useReducedMotion();
  const G = "#00D54B";
  // deterministic dot field on a 9-col grid, skipping the 3 finder corners
  // and the central $ badge area.
  const dots: { x: number; y: number }[] = [];
  const inEye = (c: number, r: number) =>
    (c < 3 && r < 3) || (c > 5 && r < 3) || (c < 3 && r > 5);
  const inCenter = (c: number, r: number) =>
    c >= 3 && c <= 5 && r >= 3 && r <= 5;
  const seed = [
    0, 1, 1, 0, 1, 0, 1, 1, 0, 1, 0, 1, 1, 0, 0, 1, 0, 1, 1, 0, 1, 0, 1, 0, 1,
    1, 0, 0, 1, 1, 0, 1, 0, 1, 1, 0, 1, 1, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1, 0, 1,
    0, 1, 1, 0, 1, 1, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1, 0, 1, 1, 0, 1, 1, 0, 1, 0,
    1, 1, 0, 1, 0, 1,
  ];
  let k = 0;
  for (let r = 0; r < 9; r++) {
    for (let c = 0; c < 9; c++) {
      if (inEye(c, r) || inCenter(c, r)) continue;
      if (seed[k++ % seed.length])
        dots.push({ x: 6 + c * 9.6, y: 6 + r * 9.6 });
    }
  }
  const Eye = ({ x, y }: { x: number; y: number }) => (
    <>
      <rect
        x={x}
        y={y}
        width="22"
        height="22"
        rx="7"
        fill="none"
        stroke={G}
        strokeWidth="3.4"
      />
      <rect x={x + 7} y={y + 7} width="8" height="8" rx="3" fill={G} />
    </>
  );
  return (
    <div className="relative overflow-hidden rounded-2xl bg-white p-2.5 shadow-lg">
      <svg width="96" height="96" viewBox="0 0 96 96">
        <Eye x={4} y={4} />
        <Eye x={70} y={4} />
        <Eye x={4} y={70} />
        {dots.map((d, i) => (
          <circle key={i} cx={d.x} cy={d.y} r={2.6} fill={G} />
        ))}
        <rect x={36} y={36} width="24" height="24" rx="7" fill={G} />
        <text
          x={48}
          y={57}
          textAnchor="middle"
          fontSize="20"
          fontWeight="800"
          fill="#fff"
          fontFamily="Arial, sans-serif"
        >
          $
        </text>
      </svg>
      {/* scan line — sweeps the code like a live reader */}
      {!reduce && (
        <motion.div
          aria-hidden
          className="pointer-events-none absolute inset-x-1.5 h-[3px] rounded-full bg-[#00D54B]"
          style={{ boxShadow: "0 0 10px 2px rgba(0,213,75,0.7)" }}
          initial={{ top: "10%", opacity: 0 }}
          animate={{ top: ["10%", "88%", "10%"], opacity: [0, 1, 1, 0] }}
          transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}
        />
      )}
    </div>
  );
}

// Rising area sparkline (animated draw) for the Earn tile.
/* ───────────────── Earn yield card ─────────────────
   A "put money to work" panel: pointer-tracked 3D tilt + green spotlight
   (mouse only), live-estimate header, count-up APY, a gradient growth chart
   that draws in over staggered gridlines, floating "+$" yield pops, and a
   projection row whose green fill sweeps in as the number counts up.
   Numbers are tabular, the chart carries a text summary, and every
   loop/tilt/draw is skipped under reduced-motion. Figures are illustrative
   (noted below). */
const YIELD_POPS = ["+$0.42", "+$1.07", "+$0.23", "+$0.88", "+$0.61"];

function YieldPops({ reduce = false }: { reduce?: boolean | null }) {
  const [i, setI] = useState(-1);
  useEffect(() => {
    if (reduce) return;
    const t = setInterval(() => setI((v) => (v + 1) % YIELD_POPS.length), 3200);
    return () => clearInterval(t);
  }, [reduce]);
  if (reduce || i < 0) return null;
  return (
    <motion.span
      key={i}
      aria-hidden
      initial={{ opacity: 0, y: 10, scale: 0.85 }}
      animate={{ opacity: [0, 1, 1, 0], y: -34, scale: 1 }}
      transition={{ duration: 2.4, ease: "easeOut", times: [0, 0.15, 0.7, 1] }}
      className="pointer-events-none absolute right-1 -top-1 rounded-full border border-[#61CE70]/25 bg-[#0A0E15]/90 px-2 py-0.5 text-[10px] font-bold text-[#9BE8A6] tabular-nums shadow-[0_4px_16px_-4px_rgba(97,206,112,0.45)]"
    >
      {YIELD_POPS[i]}
    </motion.span>
  );
}

function EarnYieldCard({ reduce = false }: { reduce?: boolean | null }) {
  const d =
    "M0,98 C24,94 40,90 64,82 C88,74 104,70 128,60 C152,50 168,44 192,32 C216,20 236,14 258,8";

  // pointer-tracked tilt + spotlight (mouse only, spring-smoothed)
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const mx = useMotionValue(50);
  const my = useMotionValue(40);
  const tiltX = useSpring(rx, { stiffness: 220, damping: 24 });
  const tiltY = useSpring(ry, { stiffness: 220, damping: 24 });
  const spotlight = useMotionTemplate`radial-gradient(240px circle at ${mx}% ${my}%, rgba(97,206,112,0.1), transparent 70%)`;

  const onMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (reduce || e.pointerType !== "mouse") return;
    const r = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width;
    const py = (e.clientY - r.top) / r.height;
    ry.set((px - 0.5) * 8);
    rx.set((0.5 - py) * 6);
    mx.set(px * 100);
    my.set(py * 100);
  };
  const onLeave = () => {
    rx.set(0);
    ry.set(0);
  };

  return (
    <div className="[perspective:1100px]">
      <motion.div
        onPointerMove={onMove}
        onPointerLeave={onLeave}
        style={
          reduce
            ? undefined
            : { rotateX: tiltX, rotateY: tiltY, transformStyle: "preserve-3d" }
        }
        whileHover={reduce ? undefined : { y: -4 }}
        transition={{ type: "spring", stiffness: 260, damping: 22 }}
        className="group relative overflow-hidden rounded-2xl border border-white/10 bg-[#0A0E15]/70 p-5 sm:p-6 shadow-[0_24px_60px_-30px_rgba(0,0,0,0.85)] transition-colors duration-300 hover:border-[#61CE70]/30 will-change-transform"
        aria-label="Illustrative: an estimated 8.4% APY grows $1,000 to about $1,084 over 12 months"
      >
        {/* breathing corner glow */}
        <motion.div
          aria-hidden
          className="pointer-events-none absolute -right-12 -top-12 w-44 h-44 rounded-full bg-[#61CE70]/10 blur-3xl"
          animate={
            reduce ? undefined : { opacity: [0.5, 1, 0.5], scale: [1, 1.12, 1] }
          }
          transition={{ duration: 4.2, repeat: Infinity, ease: "easeInOut" }}
        />
        {/* cursor spotlight */}
        {!reduce && (
          <motion.div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
            style={{ background: spotlight }}
          />
        )}
        {/* hover sheen sweep */}
        <div className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/[0.06] to-transparent transition-transform duration-[900ms] ease-out group-hover:translate-x-full" />

        {/* header: live estimate + trend badge */}
        <div className="relative flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500">
            <span className="relative flex h-1.5 w-1.5">
              {!reduce && (
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#61CE70] opacity-70" />
              )}
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#61CE70]" />
            </span>
            Live estimate
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[#61CE70]/25 bg-[#61CE70]/10 px-2.5 py-1 text-xs font-bold text-[#9BE8A6]">
            <motion.span
              className="inline-flex"
              animate={reduce ? undefined : { y: [0, -1.5, 0] }}
              transition={{
                duration: 2.2,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <TrendingUp size={13} strokeWidth={2.5} />
            </motion.span>{" "}
            Growing
          </span>
        </div>

        {/* APY */}
        <div className="relative mt-3">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500">
            Est. APY
          </div>
          <div className="mt-1 flex items-baseline gap-1">
            <CountUp
              to={8.4}
              decimals={1}
              className="text-4xl sm:text-5xl font-extrabold text-white tabular-nums leading-none"
            />
            <span className="text-2xl font-extrabold text-[#61CE70] leading-none [text-shadow:0_0_18px_rgba(97,206,112,0.45)]">
              %
            </span>
            <span className="ml-1.5 text-sm font-semibold text-gray-500">
              / yr
            </span>
          </div>
        </div>

        {/* growth chart */}
        <div className="relative mt-4">
          <YieldPops reduce={reduce} />
          <svg
            viewBox="0 0 258 110"
            className="w-full h-[104px] overflow-visible"
            preserveAspectRatio="none"
            role="img"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id="earn-fill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#61CE70" stopOpacity="0.35" />
                <stop offset="100%" stopColor="#61CE70" stopOpacity="0" />
              </linearGradient>
              <linearGradient id="earn-stroke" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#61CE70" />
                <stop offset="100%" stopColor="#9BE8A6" />
              </linearGradient>
            </defs>
            {[27, 55, 83].map((y, i) => (
              <motion.line
                key={y}
                x1="0"
                y1={y}
                x2="258"
                y2={y}
                stroke="rgba(255,255,255,0.05)"
                strokeWidth="1"
                initial={reduce ? false : { pathLength: 0, opacity: 0 }}
                whileInView={reduce ? undefined : { pathLength: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.8,
                  delay: 0.1 + i * 0.12,
                  ease: "easeOut",
                }}
              />
            ))}
            <motion.path
              d={`${d} L258,110 L0,110 Z`}
              fill="url(#earn-fill)"
              initial={reduce ? false : { opacity: 0 }}
              whileInView={reduce ? undefined : { opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.7, ease: "easeOut" }}
            />
            <motion.path
              d={d}
              fill="none"
              stroke="url(#earn-stroke)"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ filter: "drop-shadow(0 0 5px rgba(97,206,112,0.5))" }}
              initial={reduce ? false : { pathLength: 0 }}
              whileInView={reduce ? undefined : { pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.3, ease: "easeOut" }}
            />
            {/* pulsing halo + solid end-dot */}
            <motion.circle
              cx="258"
              cy="8"
              r="7"
              fill="#61CE70"
              opacity="0.35"
              animate={
                reduce
                  ? undefined
                  : { r: [7, 12, 7], opacity: [0.35, 0.05, 0.35] }
              }
              transition={{
                duration: 2.4,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
            <circle cx="258" cy="8" r="4" fill="#61CE70" />
          </svg>
          <div className="mt-2 flex items-center justify-between text-[10px] font-medium uppercase tracking-wide text-gray-600">
            <span>Now</span>
            <span>12 months</span>
          </div>
        </div>

        {/* concrete projection — fill sweeps in while the number counts up */}
        <div className="relative mt-4 overflow-hidden rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-3">
          <motion.div
            aria-hidden
            className="absolute inset-0 origin-left bg-gradient-to-r from-[#61CE70]/[0.04] via-[#61CE70]/[0.07] to-[#61CE70]/[0.14]"
            initial={reduce ? false : { scaleX: 0 }}
            whileInView={reduce ? undefined : { scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.6, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          />
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-400 tabular-nums">$1,000</span>
              <motion.span
                className="inline-flex"
                animate={reduce ? undefined : { x: [0, 3, 0] }}
                transition={{
                  duration: 1.4,
                  repeat: Infinity,
                  repeatDelay: 2.6,
                  ease: "easeInOut",
                }}
              >
                <ArrowRight size={14} className="text-[#61CE70]/70" />
              </motion.span>
              <CountUp
                from={1000}
                to={1084}
                duration={1.6}
                prefix="$"
                className="font-bold text-white tabular-nums"
              />
            </div>
            <span className="text-[11px] text-gray-500">in 12 months</span>
          </div>
        </div>

        <p className="relative mt-2.5 text-[10px] leading-relaxed text-gray-600">
          Illustrative estimate. Actual yield varies and isn't guaranteed.
        </p>
      </motion.div>
    </div>
  );
}

/* ───────────────── green cursor dot (same accent as the CashTap dashboard) ─────────────────
   Follows the pointer with eased lerp, plus a slower trailing ring. Driven by a
   single rAF writing transforms directly (no per-frame React re-render, so it
   never taxes the Three.js hero). Mouse-only + reduced-motion aware. */
function CursorDot() {
  const dotRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();

  useEffect(() => {
    if (reduce) return;
    if (
      typeof window === "undefined" ||
      !window.matchMedia("(pointer: fine)").matches
    )
      return;

    const target = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    const dot = { ...target };
    const ring = { ...target };
    const lerp = (a: number, b: number, f: number) => a + (b - a) * f;
    let raf = 0;

    const onMove = (e: MouseEvent) => {
      target.x = e.clientX;
      target.y = e.clientY;
    };
    const loop = () => {
      dot.x = lerp(dot.x, target.x, 0.22);
      dot.y = lerp(dot.y, target.y, 0.22);
      ring.x = lerp(ring.x, target.x, 0.12);
      ring.y = lerp(ring.y, target.y, 0.12);
      if (dotRef.current)
        dotRef.current.style.transform = `translate3d(${dot.x - 5}px, ${dot.y - 5}px, 0)`;
      if (ringRef.current)
        ringRef.current.style.transform = `translate3d(${ring.x - 17}px, ${ring.y - 17}px, 0)`;
      raf = requestAnimationFrame(loop);
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    raf = requestAnimationFrame(loop);
    return () => {
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(raf);
    };
  }, [reduce]);

  if (reduce) return null;
  return (
    <>
      <div
        ref={ringRef}
        aria-hidden
        className="pointer-events-none fixed left-0 top-0 z-[9998] hidden h-[34px] w-[34px] rounded-full border border-[#61CE70]/35 lg:block"
        style={{ willChange: "transform" }}
      />
      <div
        ref={dotRef}
        aria-hidden
        className="pointer-events-none fixed left-0 top-0 z-[9999] hidden h-2.5 w-2.5 rounded-full bg-[#61CE70] lg:block"
        style={{
          willChange: "transform",
          boxShadow: "0 0 12px rgba(97,206,112,0.7)",
        }}
      />
    </>
  );
}

/* Number that counts up from zero once scrolled into view. */
function CountUp({
  to,
  from = 0,
  prefix = "",
  suffix = "",
  decimals = 0,
  duration = 1.4,
  className = "",
}: {
  to: number;
  from?: number;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  duration?: number;
  className?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const reduce = useReducedMotion();
  const mv = useMotionValue(from);
  const text = useTransform(
    mv,
    (v) =>
      `${prefix}${Number(v).toLocaleString("en-US", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })}${suffix}`,
  );

  useEffect(() => {
    if (!inView) return;
    if (reduce) {
      mv.set(to);
      return;
    }
    const controls = animate(mv, to, { duration, ease: [0.16, 1, 0.3, 1] });
    return () => controls.stop();
  }, [inView, to, duration, reduce, mv]);

  return (
    <motion.span ref={ref} className={className}>
      {text}
    </motion.span>
  );
}

/* Unified live-earnings dashboard card: a count-up "processed today" figure,
   a 7-day trend chart that draws in, and a 2×2 stat strip — one cohesive
   product surface that mirrors the real dashboard's language. */
function LiveEarningsPanel() {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const mv = useMotionValue(0);
  const amount = useTransform(
    mv,
    (v) => `$${Math.round(v).toLocaleString("en-US")}`,
  );

  useEffect(() => {
    if (!inView) return;
    if (reduce) {
      mv.set(12480);
      return;
    }
    const controls = animate(mv, 12480, {
      duration: 1.7,
      ease: [0.16, 1, 0.3, 1],
    });
    return () => controls.stop();
  }, [inView, reduce, mv]);

  return (
    <div
      ref={ref}
      className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-7"
      style={{
        boxShadow:
          "0 40px 90px -40px rgba(0,0,0,0.7), inset 0 0 0 1px rgba(255,255,255,0.03)",
      }}
    >
      <div className="pointer-events-none absolute -right-16 -top-16 h-44 w-44 rounded-full bg-[#61CE70]/10 blur-3xl" />

      {/* header + live total */}
      <div className="relative flex items-start justify-between">
        <div>
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500">
            <span className="relative flex h-1.5 w-1.5">
              {!reduce && (
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#61CE70] opacity-60" />
              )}
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#61CE70]" />
            </span>
            Processed today
          </div>
          <div className="mt-1.5 flex items-end gap-2">
            <motion.span className="text-4xl font-extrabold tracking-tight text-white tabular-nums">
              {amount}
            </motion.span>
            <span className="mb-1.5 inline-flex items-center gap-1 text-xs font-bold text-[#61CE70]">
              <TrendingUp size={13} /> +12%
            </span>
          </div>
        </div>
        <span className="rounded-full bg-white/5 px-2.5 py-1 text-[10px] font-bold text-gray-300">
          7D
        </span>
      </div>

      {/* 7-day trend */}
      <div className="relative mt-6 flex h-[120px] items-end gap-2">
        {WEEK.map((h, i) => {
          const last = i === WEEK.length - 1;
          return (
            <div
              key={i}
              className="flex h-full flex-1 flex-col items-center justify-end gap-2"
            >
              <motion.div
                initial={{ height: 0 }}
                whileInView={{ height: `${h}%` }}
                viewport={{ once: true }}
                transition={{
                  type: "spring",
                  stiffness: 120,
                  damping: 18,
                  delay: 0.15 + i * 0.06,
                }}
                className={`w-full rounded-md ${
                  last
                    ? "bg-[#61CE70]"
                    : "bg-gradient-to-t from-[#61CE70]/20 to-[#61CE70]/65"
                }`}
                style={
                  last
                    ? { boxShadow: "0 0 16px rgba(97,206,112,0.6)" }
                    : undefined
                }
              />
              <span className="text-[9px] leading-none text-gray-600">
                {DAY_LABELS[i]}
              </span>
            </div>
          );
        })}
      </div>

      {/* stat strip */}
      <div className="relative mt-6 grid grid-cols-2 gap-3 border-t border-white/[0.08] pt-5">
        {STATS.map((s, i) => (
          <motion.div
            key={s.label}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.08 }}
            className="rounded-xl border border-white/[0.08] bg-white/[0.03] px-3.5 py-3"
          >
            <div className="flex items-center gap-1.5 text-gray-500">
              <s.icon size={13} className="text-[#61CE70]" />
              <span className="text-[10px]">{s.label}</span>
            </div>
            <div className="mt-1 text-xl font-extrabold tabular-nums text-[#61CE70]">
              {s.count != null ? (
                <CountUp to={s.count} suffix={s.suffix || ""} />
              ) : (
                s.value
              )}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

/* Payment-link rows that light up one at a time, like live incoming payments. */
const LINK_ROWS = [
  { label: "Payment request", amt: "$120.00" },
  { label: "Tip jar", amt: "$8.00" },
  { label: "Invoice #1042", amt: "$540.00" },
  { label: "Donation", amt: "$25.00" },
];
function LivePaymentRows() {
  const reduce = useReducedMotion();
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (reduce) return;
    const id = setInterval(
      () => setActive((a) => (a + 1) % LINK_ROWS.length),
      2200,
    );
    return () => clearInterval(id);
  }, [reduce]);

  return (
    <div className="mt-auto grid gap-2.5 pt-6 sm:grid-cols-2">
      {LINK_ROWS.map((r, i) => {
        const on = !reduce && i === active;
        return (
          <div
            key={r.label}
            className={`flex items-center justify-between rounded-xl px-3 py-2 ring-1 transition-all duration-500 ${
              on
                ? "bg-[#61CE70]/10 ring-[#61CE70]/40"
                : "bg-white/[0.04] ring-white/5"
            }`}
          >
            <span className="flex items-center gap-2 text-[12px] text-gray-300">
              <span
                className={`h-1.5 w-1.5 rounded-full bg-[#61CE70] ${on ? "animate-pulse" : ""}`}
              />
              {r.label}
            </span>
            <span className="text-right leading-tight">
              <span className="block text-[12px] font-bold tabular-nums text-[#61CE70]">
                {r.amt}
              </span>
              <span
                className={`block text-[9px] leading-none text-gray-500 transition-opacity duration-300 ${
                  on ? "opacity-100" : "opacity-0"
                }`}
              >
                just now
              </span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

/* Tap-to-pay ripple for the Apple Pay / Google Pay tile. */
function NfcRipple() {
  const reduce = useReducedMotion();
  return (
    <div className="mt-auto flex items-center justify-center pt-6">
      <div className="relative flex h-24 w-24 items-center justify-center">
        {!reduce &&
          [0, 1, 2].map((i) => (
            <motion.span
              key={i}
              className="absolute rounded-full border border-[#61CE70]/50"
              initial={{ width: 30, height: 30, opacity: 0.6 }}
              animate={{ width: 92, height: 92, opacity: 0 }}
              transition={{
                duration: 2.4,
                repeat: Infinity,
                delay: i * 0.8,
                ease: "easeOut",
              }}
            />
          ))}
        <div className="relative flex h-11 w-11 items-center justify-center rounded-full bg-[#61CE70]/15">
          <Nfc size={20} className="text-[#61CE70]" />
        </div>
      </div>
    </div>
  );
}

/* Equalizer-style energy bars for the Lightning tile. */
function LightningPulse() {
  const reduce = useReducedMotion();
  return (
    <div className="mt-auto flex h-10 items-end gap-1.5 pt-6">
      {[0, 1, 2, 3, 4, 5, 6].map((i) => (
        <motion.span
          key={i}
          className="h-full flex-1 origin-bottom rounded-full bg-gradient-to-t from-[#61CE70]/20 to-[#61CE70]/70"
          animate={
            reduce ? { scaleY: 0.6 } : { scaleY: [0.4, 1, 0.55, 0.9, 0.45] }
          }
          transition={{
            duration: 1.6,
            repeat: Infinity,
            ease: "easeInOut",
            delay: i * 0.12,
          }}
        />
      ))}
    </div>
  );
}

/* How-it-works step chips — small live illustrations under each step. */
function StepAccountChip() {
  return (
    <div className="mx-auto mt-6 flex max-w-[240px] items-center gap-2.5 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#61CE70] to-[#3aa64a] text-xs font-black text-[#06250f]">
        Y
      </span>
      <div className="min-w-0 flex-1 text-left">
        <div className="truncate text-[12px] font-semibold text-white">
          @you
        </div>
        <div className="truncate text-[10px] text-gray-500">
          cashtap.cash/you
        </div>
      </div>
      <motion.span
        initial={{ scale: 0 }}
        whileInView={{ scale: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.4, type: "spring", stiffness: 320, damping: 15 }}
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#61CE70]"
      >
        <Check size={12} className="text-[#06250f]" strokeWidth={3.5} />
      </motion.span>
    </div>
  );
}

function StepShareChip() {
  const reduce = useReducedMotion();
  return (
    <div className="mx-auto mt-6 flex max-w-[240px] items-center gap-2.5 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#61CE70]/12">
        <Link2 size={16} className="text-[#61CE70]" />
      </span>
      <div className="min-w-0 flex-1 truncate text-left font-mono text-[12px] text-gray-300">
        cashtap.cash/you
      </div>
      <motion.span
        animate={reduce ? {} : { scale: [1, 1.15, 1] }}
        transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#61CE70]/15"
      >
        <Share2 size={14} className="text-[#61CE70]" />
      </motion.span>
    </div>
  );
}

function StepPaidChip() {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const mv = useMotionValue(0);
  const text = useTransform(mv, (v) => `+$${v.toFixed(2)}`);

  useEffect(() => {
    if (!inView) return;
    if (reduce) {
      mv.set(42);
      return;
    }
    const controls = animate(mv, 42, {
      duration: 1.2,
      ease: [0.16, 1, 0.3, 1],
    });
    return () => controls.stop();
  }, [inView, reduce, mv]);

  return (
    <div
      ref={ref}
      className="mx-auto mt-6 flex max-w-[240px] items-center gap-2.5 rounded-xl border border-[#61CE70]/25 bg-[#61CE70]/[0.06] px-3 py-2.5"
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white">
        <img
          src="/cashapp-logo.png"
          alt=""
          className="max-h-4 max-w-5 object-contain"
        />
      </span>
      <div className="min-w-0 flex-1 text-left">
        <div className="truncate text-[12px] font-semibold text-white">
          Payment received
        </div>
        <div className="text-[10px] text-gray-500">CashApp · just now</div>
      </div>
      <motion.span className="text-sm font-bold tabular-nums text-[#61CE70]">
        {text}
      </motion.span>
    </div>
  );
}

/* ───────────────── Animated U.S. account card ─────────────────
   The CashTap Account card as a Remotion-rendered clip (square, dark bg that
   blends into the banking section). Autoplays + loops; reduced-motion users
   get a static late frame where the card is fully assembled (not the empty
   first frame). */
function AccountCardVideo() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const reduce = useReducedMotion();

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (reduce) {
      v.pause();
      const showBuilt = () => {
        try {
          v.currentTime = 5.2; // fully-assembled frame (clip is 6s)
        } catch {
          /* metadata not ready — harmless */
        }
      };
      if (v.readyState >= 1) showBuilt();
      else v.addEventListener("loadedmetadata", showBuilt, { once: true });
    } else {
      v.play().catch(() => {
        /* autoplay may be blocked until interaction — fine */
      });
    }
  }, [reduce]);

  return (
    <video
      ref={videoRef}
      className="block w-full h-auto"
      src="/videos/account-card.mp4"
      autoPlay={!reduce}
      loop
      muted
      playsInline
      preload="auto"
      aria-label="An animated CashTap U.S. bank account with routing and account number, ACH, wire and direct deposit"
    />
  );
}

/* ───────────────── Remotion video card ─────────────────
   Autoplaying, looping, muted product clip with a hover lift + caption. Honors
   reduced-motion by pausing on the first frame. */
function VideoCard({
  src,
  title,
  subtitle,
  aspect = "aspect-square",
  className = "",
  delay = 0,
  chromeUrl,
}: {
  src: string;
  title: string;
  subtitle: string;
  aspect?: string;
  className?: string;
  delay?: number;
  chromeUrl?: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const reduce = useReducedMotion();

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (reduce) {
      v.pause();
      try {
        v.currentTime = 0.05;
      } catch {
        /* metadata not ready yet — harmless */
      }
    } else {
      v.play().catch(() => {
        /* autoplay may be blocked until interaction — fine */
      });
    }
  }, [reduce]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }}
      className={`group relative overflow-hidden rounded-2xl border border-white/10 bg-[#0B0E14] shadow-[0_30px_70px_-30px_rgba(0,0,0,0.8)] transition-transform duration-300 hover:-translate-y-1 ${className}`}
    >
      {chromeUrl && (
        <div className="flex items-center gap-1.5 border-b border-white/10 px-4 py-3">
          <span className="h-3 w-3 rounded-full bg-red-400/70" />
          <span className="h-3 w-3 rounded-full bg-yellow-400/70" />
          <span className="h-3 w-3 rounded-full bg-green-400/70" />
          <div className="mx-auto flex items-center gap-1.5 rounded-md bg-white/5 px-3 py-1 text-[11px] text-gray-400">
            <Lock size={11} className="text-[#61CE70]" />
            {chromeUrl}
          </div>
        </div>
      )}
      <video
        ref={videoRef}
        className={`w-full ${aspect} object-cover`}
        src={src}
        autoPlay={!reduce}
        loop
        muted
        playsInline
        preload="auto"
        aria-hidden
      />
      {!chromeUrl && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent p-5 pt-12">
          <div className="text-sm font-bold text-white">{title}</div>
          <div className="text-xs text-gray-400">{subtitle}</div>
        </div>
      )}
      <div className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-white/5 transition group-hover:ring-[#61CE70]/30" />
    </motion.div>
  );
}

/* Live webhook log for the developers section — rows stream in on a loop. */
function DevEventStream() {
  const reduce = useReducedMotion();
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (reduce) return;
    const id = setInterval(() => setTick((t) => t + 1), 1700);
    return () => clearInterval(id);
  }, [reduce]);

  const rows = [0, 1, 2].map((k) => DEV_EVENTS[(tick + k) % DEV_EVENTS.length]);

  return (
    <div className="mt-7 rounded-2xl border border-white/10 bg-[#0B0E14] p-4">
      <div className="mb-3 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500">
        <span className="relative flex h-1.5 w-1.5">
          {!reduce && (
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#61CE70] opacity-60" />
          )}
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#61CE70]" />
        </span>
        Webhooks · listening
      </div>
      {/* The real event names are long (26 chars). On phones the row has
          ~230px, so the POST verb is dropped and the type steps down a notch;
          `truncate` is the safety net for anything narrower still. */}
      <div className="space-y-2 font-mono text-[11.5px] sm:text-[12.5px]">
        {rows.map((r, k) => (
          <motion.div
            key={`${tick}-${k}`}
            initial={reduce ? false : { opacity: 0, y: -6 }}
            animate={{ opacity: k === 0 ? 1 : 0.45, y: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="flex items-center justify-between gap-2"
          >
            <span className="flex min-w-0 items-center gap-2 text-gray-300">
              <span className="hidden text-gray-600 sm:inline">POST</span>
              <span className="truncate">{r.ev}</span>
            </span>
            <span className="shrink-0 rounded bg-[#61CE70]/15 px-2 py-0.5 text-[11px] font-bold text-[#61CE70]">
              {r.code}
            </span>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

/* Payment chips that drift upward behind the final CTA — a subtle "money
   coming in" backdrop. Fixed positions, looped rise + fade, reduced-motion off. */
const CTA_CHIPS = [
  { who: "@sarah", amt: "+$26", left: "8%", delay: 0 },
  { who: "Apple Pay", amt: "+$54", left: "26%", delay: 1.6 },
  { who: "@emma", amt: "+$18", left: "68%", delay: 0.8 },
  { who: "CashApp", amt: "+$120", left: "86%", delay: 2.4 },
  { who: "@noah", amt: "+$9", left: "48%", delay: 3.1 },
];
function FloatingChips() {
  const reduce = useReducedMotion();
  if (reduce) return null;
  return (
    <div
      className="pointer-events-none absolute inset-0 overflow-hidden"
      aria-hidden
    >
      {CTA_CHIPS.map((c) => (
        <motion.div
          key={c.who + c.left}
          className="absolute bottom-0 flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] px-3 py-1.5 backdrop-blur-sm"
          style={{ left: c.left }}
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: [-10, -220], opacity: [0, 1, 1, 0] }}
          transition={{
            duration: 7,
            delay: c.delay,
            repeat: Infinity,
            ease: "easeOut",
            times: [0, 0.12, 0.75, 1],
          }}
        >
          <span className="text-[11px] font-medium text-gray-300">{c.who}</span>
          <span className="text-[11px] font-bold text-[#61CE70]">{c.amt}</span>
        </motion.div>
      ))}
    </div>
  );
}
